import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Customer } from '../models/customer';
import { PaginatedResponse } from '../models/paginatedResponse';
import { CustomerCreateRequest } from '../models/api/customerCreateRequest';
import { CustomerUpdateRequest } from '../models/api/customerUpdateRequest';
import { CustomerCompanyDto } from '../models/api/customerCompanyDto';
import { CustomerCompanyCreateRequest } from '../models/api/customerCompanyCreateRequest';
import { CustomerCompanyUpdateRequest } from '../models/api/customerCompanyUpdateRequest';
import { ReportVariableCategorySummaryDto } from '../models/api/reportVariable';
import { BulkDeleteResult } from '../models/api/bulkDeleteResult';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class CustomerService {
  private http = inject(HttpClient);
  private readonly API_BASE_URL = `${environment.apiUrl}/customers`;
  private readonly CUSTOMER_COMPANIES_URL = `${environment.apiUrl}/customer-companies`;

  getCustomers(
    pageNumber: number = 1,
    pageSize: number = 50,
    sortBy: string = 'Id',
    ascending: boolean = true
  ): Observable<PaginatedResponse<Customer>> {
    const params = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString())
      .set('sortBy', sortBy)
      .set('ascending', ascending.toString());

    return this.http.get<PaginatedResponse<Customer>>(this.API_BASE_URL, { params });
  }

  getCustomer(id: string): Observable<Customer> {
    return this.http.get<Customer>(`${this.API_BASE_URL}/${id}`);
  }

  searchCustomers(searchTerm: string): Observable<Customer[]> {
    const params = new HttpParams().set('searchTerm', searchTerm);
    return this.http.get<Customer[]>(`${this.API_BASE_URL}/search`, { params });
  }

  createCustomer(request: CustomerCreateRequest): Observable<Customer> {
    return this.http.post<Customer>(this.API_BASE_URL, request);
  }

  updateCustomer(id: string, request: CustomerUpdateRequest): Observable<Customer> {
    return this.http.put<Customer>(`${this.API_BASE_URL}/${id}`, request);
  }

  deleteCustomer(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/${id}`);
  }

  deleteCustomers(ids: string[]): Observable<BulkDeleteResult> {
    return this.http.post<BulkDeleteResult>(`${this.API_BASE_URL}/bulk-delete`, { ids });
  }

  getCustomerCompany(id: string): Observable<CustomerCompanyDto> {
    return this.http.get<CustomerCompanyDto>(`${this.CUSTOMER_COMPANIES_URL}/${id}`);
  }

  createCustomerCompany(request: CustomerCompanyCreateRequest): Observable<CustomerCompanyDto> {
    return this.http.post<CustomerCompanyDto>(this.CUSTOMER_COMPANIES_URL, request);
  }

  updateCustomerCompany(id: string, request: CustomerCompanyUpdateRequest): Observable<CustomerCompanyDto> {
    return this.http.put<CustomerCompanyDto>(`${this.CUSTOMER_COMPANIES_URL}/${id}`, request);
  }

  deleteCustomerCompany(id: string): Observable<void> {
    return this.http.delete<void>(`${this.CUSTOMER_COMPANIES_URL}/${id}`);
  }

  getReportVariableCategories(): Observable<ReportVariableCategorySummaryDto[]> {
    return this.http.get<ReportVariableCategorySummaryDto[]>(
      `${environment.apiUrl}/report-variable-categories`);
  }
}
